/* eslint-disable no-console */
/* eslint-disable lit/no-value-attribute */

import "@bodil/monkey-business";

import { time } from "@bodil/core";
import { assert, isEmpty, present } from "@bodil/core/assert";
import { Component, reactive, type Deps } from "@bodil/dom/component";
import { eventListener } from "@bodil/dom/event";
import { Signal } from "@bodil/signal";
import createFuzzySearch from "@nozbe/microfuzz";
import { downloadZip } from "client-zip";
import { html } from "lit";
import { customElement } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";

import { docs, settings, type Program } from "./db";
import type { BcDocument } from "./document";
import { BcHelp } from "./help";
import { BcIcon } from "./icon";

import styles from "./app.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "bc-app": BcApp;
    }
}

@customElement("bc-app")
export class BcApp extends Component {
    static deps: Deps = [BcHelp, BcIcon];

    @reactive currentDoc?: BcDocument;
    @reactive currentDocId?: string;
    @reactive maximise = settings.get("maximise", false);
    @reactive editingTitle = false;
    @reactive helpVisible = false;
    @reactive orderBy = settings.get<"title" | "mtime">("orderBy", "mtime");
    @reactive filter = "";

    docsQuery: Signal.Computed<Readonly<Array<Program>>> = Signal.computed(() => {
        const items = docs.orderBy(this.orderBy.get()).signal().get();
        if (this.orderBy.get() === "mtime") {
            items.reverse();
        }
        if (isEmpty(this.filter)) {
            return items;
        }
        const search = createFuzzySearch(items, { key: "title", strategy: "aggressive" });
        return search(this.filter).map((result) => result.item);
    });

    openDocs = new Map<string, BcDocument>();

    private viewTransition = false;
    private ignoreHashChange = false;

    renameInput = createRef<HTMLInputElement>();
    searchInput = createRef<HTMLInputElement>();
    documentSelector = createRef<HTMLSelectElement>();

    get selectedDocs(): Array<string> {
        const selection = this.documentSelector.value?.selectedOptions;
        return selection === undefined ? [] : Array.from(selection).map((el) => el.value);
    }

    get locationHash(): string | undefined {
        const id = location.hash.slice(1);
        return id.length > 0 ? id : undefined;
    }

    constructor() {
        super();
        import("./document").catch((e) => console.error("Failed to load CodeMirror module:", e));
    }

    protected override connected() {
        this.useWhileConnected(
            eventListener(window, "hashchange", this.handleHashChange.bind(this)),
        );
    }

    protected override firstUpdated() {
        if (this.locationHash !== undefined) {
            this.handleHashChange();
        }
        this.viewTransition = true;
    }

    openDocument(id: string, options?: { noPushState: boolean }) {
        this.ignoreHashChange = true;
        this.currentDoc = this.openDocs.get(id) ?? this.createDocumentEditor(id);
        this.currentDocId = id;
        this.requestUpdate({ viewTransition: this.viewTransition });
        if (options?.noPushState !== true) {
            window.history.pushState(true, "", `#${id}`);
        }
        this.updateComplete
            .then(() => {
                this.ignoreHashChange = false;
                this.currentDoc?.focus();
            })
            .catch(console.error);
    }

    closeDocument(options?: { noPushState: boolean }) {
        this.ignoreHashChange = true;
        const id = present(this.currentDocId);
        this.currentDoc = undefined;
        this.currentDocId = undefined;
        this.requestUpdate({ viewTransition: this.viewTransition });
        if (options?.noPushState !== true) {
            if (window.history.state === true) {
                window.history.back();
            } else {
                window.history.pushState(null, "", "#");
            }
        }
        this.updateComplete
            .then(() => {
                this.ignoreHashChange = false;
                const select = present(this.documentSelector.value);
                select.selectedIndex = select.namedItem(id)?.index ?? -1;
                select.focus();
                this.requestUpdate();
            })
            .catch(console.error);
    }

    createDocumentEditor(id: string): BcDocument {
        assert(!this.openDocs.has(id));
        const doc = document.createElement("bc-document");
        doc.setAttribute("doc", id);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        doc.addEventListener("cancel", this.closeDocument.bind(this) as () => void);
        this.openDocs.set(id, doc);
        return doc;
    }

    toggleMaximise() {
        settings.set("maximise", !this.maximise.get());
        this.requestUpdate({ viewTransition: this.viewTransition });
    }

    private fileFromDoc(doc: Program): File {
        return new File([doc.code], `${doc.title}.txt`, {
            type: "text/plain",
            endings: "native",
            lastModified: doc.mtime.epochMilliseconds,
        });
    }

    private async downloadSelection(): Promise<File> {
        if (this.selectedDocs.length > 1) {
            const seenNames = new Set<string>();
            const files = this.selectedDocs.map((id) => {
                const doc = { ...present(docs.get(id)) };
                let attempt = 0;
                let name = doc.title;
                while (seenNames.has(name)) {
                    name = `${doc.title} (${++attempt})`;
                }
                seenNames.add(name);
                doc.title = name;
                return this.fileFromDoc(doc);
            });
            return new File([await downloadZip(files).blob()], "documents.zip", {
                type: "application/zip",
            });
        }
        return this.fileFromDoc(present(docs.get(this.selectedDocs[0])));
    }

    private handleHashChange() {
        if (this.ignoreHashChange) {
            return;
        }
        if (this.locationHash === undefined) {
            this.closeDocument({ noPushState: true });
        } else if (docs.where("id").equals(this.locationHash).toArray().length > 0) {
            this.openDocument(this.locationHash, { noPushState: true });
        }
    }

    private handleAddDocument() {
        let title: string;
        let counter = 0;
        do {
            title = `Untitled ${++counter}`;
        } while (docs.where("title").equals(title).toArray().length > 0);
        const doc = {
            id: crypto.randomUUID(),
            ctime: time.now(),
            mtime: time.now(),
            title,
            code: "",
        };
        docs.add(doc);
        this.openDocument(doc.id);
    }

    private async handleUpload() {
        const upload = present(this.query("#upload")) as HTMLInputElement;
        docs.add(
            ...(await Promise.all(
                Iterator.from(present(upload.files)).map((file) =>
                    file.text().then((code) => ({
                        id: crypto.randomUUID(),
                        ctime: time.now(),
                        mtime: time.now(),
                        title: file.name.endsWith(".txt") ? file.name.slice(0, -4) : file.name,
                        code,
                    })),
                ),
            )),
        );
    }

    private handleUploadClick() {
        present(this.query("#upload") as HTMLInputElement).click();
    }

    private async handleDownloadClick() {
        const file = await this.downloadSelection();
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.setAttribute("download", file.name);
        a.setAttribute("href", url);
        a.style = "display:none";
        document.body.append(a);
        console.log(a);
        a.click();
        setTimeout(() => {
            a.remove();
            URL.revokeObjectURL(url);
        }, 1);
    }

    private handleDeleteClick() {
        const text =
            this.selectedDocs.length > 1
                ? `${this.selectedDocs.length} documents`
                : `the document "${docs.get(this.selectedDocs[0])?.title}"`;
        // eslint-disable-next-line no-alert
        if (confirm(`Are you sure you want to PERMANENTLY delete ${text}?`)) {
            docs.delete(...this.selectedDocs);
        }
        if (
            this.currentDoc !== undefined &&
            this.selectedDocs.includes(present(this.currentDocId))
        ) {
            this.closeDocument();
        }
        // Make extra sure the buttons update when the selected item is deleted.
        void this.updateComplete.then(() => this.requestUpdate());
    }

    private handleSelectChanged() {
        this.requestUpdate();
    }

    private handleSelectDoubleClick(e: MouseEvent) {
        if (e.target instanceof HTMLOptionElement) {
            this.openDocument(e.target.value);
        }
    }

    private handleSelectKeydown(e: KeyboardEvent) {
        if (e.key === "Enter" && this.selectedDocs.length === 1) {
            this.openDocument(this.selectedDocs[0]);
        }
        if (e.key === "a" && e.ctrlKey) {
            e.preventDefault();
            Iterator.from(present(this.documentSelector.value).options).forEach((option) => {
                option.selected = true;
            });
            this.handleSelectChanged();
        }
    }

    private async handleRenameClick() {
        this.editingTitle = true;
        await this.updateComplete;
        this.renameInput.value?.focus();
        this.renameInput.value?.select();
    }

    private handleRenameCancel() {
        this.editingTitle = false;
        present(this.renameInput.value).value = present(docs.get(present(this.currentDocId))).title;
        this.currentDoc?.focus();
    }

    private handleRenameCommit() {
        this.editingTitle = false;
        docs.update(present(this.currentDocId), (doc) => {
            doc.title = present(this.renameInput.value?.value);
        });
        this.currentDoc?.focus();
    }

    private handleRenameKeydown(e: KeyboardEvent) {
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            return this.handleRenameCommit();
        }
        if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            return this.handleRenameCancel();
        }
    }

    private handleSearchInput() {
        this.filter = this.searchInput.value?.value ?? "";
    }

    private handleOrderToggle() {
        settings.set("orderBy", this.orderBy.get() === "title" ? "mtime" : "title");
    }

    private handleHelpClick() {
        this.helpVisible = !this.helpVisible;
    }

    private renderDocument(): unknown {
        const doc = present(docs.get(present(this.currentDocId)));
        return html`
            <header class=${classMap({ "edit-mode": this.editingTitle })}>
                <button title="Back to main menu" @click=${this.closeDocument}>
                    <bc-icon name="back"></bc-icon>
                </button>
                <span class="doc-title">
                    ${doc.title}
                </span>
                <input ${ref(this.renameInput)} type="text" value=${doc.title} @blur=${this.handleRenameCancel} @keydown=${this.handleRenameKeydown}></input>
                <nav class="button-bar">
                    <button id="rename" title="Rename document" @click=${this.handleRenameClick}><bc-icon name="rename"></bc-icon></button>
                    <button title="Toggle cheat sheet" @click=${this.handleHelpClick}><bc-icon name="help"></bc-icon></button>
                    <button title=${this.maximise.get() ? "Restore normal size" : "Expand to the full window size"} @click=${this.toggleMaximise}>
                        <bc-icon name=${this.maximise.get() ? "minimise" : "maximise"}></bc-icon>
                    </button>
                </nav>
            </header>
            ${this.currentDoc}
        `;
    }

    private renderSidebar(): unknown {
        return html`
            <section class="docs">
                <button @click=${this.handleAddDocument} title="New document">
                    <bc-icon name="add"></bc-icon>New
                </button>
                <input ${ref(this.searchInput)} id="search" type="search" autocomplete="off" placeholder="Filter..." @input=${this.handleSearchInput}></input>
                <select
                    ${ref(this.documentSelector)}
                    multiple
                    @change=${this.handleSelectChanged}
                    @dblclick=${this.handleSelectDoubleClick}
                    @keydown=${this.handleSelectKeydown}
                    @touchend=${this.handleSelectDoubleClick}
                >
                    ${repeat(
                        this.docsQuery.get(),
                        (doc) => doc.id,
                        (doc) =>
                            html`<option name=${doc.id} value=${doc.id} title=${doc.title}>
                                ${doc.title}
                            </option>`,
                    )}
                </select>
                <nav class="button-bar compact">
                    <button title=${`Click to order ${this.orderBy.get() === "title" ? "by last modified time" : "alphabetically"}`} @click=${this.handleOrderToggle}>
                        <bc-icon name=${this.orderBy.get() === "title" ? "orderByTitle" : "orderByTime"}></bc-icon>
                    </button>
                    <input type="file" id="upload" multiple accept="text/plain" @change=${this.handleUpload}>
                    <button title="Upload a document" @click=${this.handleUploadClick}>
                        <bc-icon name="upload"></bc-icon>
                    </button>
                    <button  title="Download documents" ?disabled=${this.selectedDocs.length === 0} @click=${this.handleDownloadClick}>
                        <bc-icon name="download"></bc-icon>
                    </button>
                    <button id="delete" ?disabled=${this.selectedDocs.length === 0} title="Delete documents" @click=${this.handleDeleteClick}>
                        <bc-icon name="delete"></bc-icon>
                    </button>
                </nav>
            </section>
        `;
    }

    protected override render(): unknown {
        return html`
            ${when(this.currentDoc === undefined, this.renderSidebar.bind(this))}
            <main class=${classMap({ maximise: this.maximise.get() })}>
                <section class="document-view">
                    ${when(
                        this.currentDoc !== undefined,
                        () => this.renderDocument(),
                        () => html`
                            <section class="splash">
                                <h1><bc-icon name="calculator"></bc-icon>bc</h1>
                                <h4>
                                    an arbitrary precision arithmetic language
                                    <i>but for the web</i>
                                </h4>
                                <p>
                                    Open a previous document from the list on the left, or click
                                    here to start a new document:
                                </p>
                                <button @click=${this.handleAddDocument} title="New document">
                                    <bc-icon name="add"></bc-icon>New Document
                                </button>
                            </section>
                            <footer>
                                <a href="https://codeberg.org/bodil/bc" target="_blank">
                                    <button title="Information about this thing">
                                        <bc-icon name="about"></bc-icon>
                                    </button>
                                </a>
                            </footer>
                        `,
                    )}
                </section>
                ${when(
                    this.helpVisible && this.currentDoc !== undefined,
                    () => html`<bc-help @close=${this.handleHelpClick}></bc-help>`,
                )}
            </main>
        `;
    }

    static styles = styles;
}
